import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

interface CodeCommon {
  codeid: number;
  codevalue: string;
  codedisplayname: string;
  categoryid: number;
  codediscription: string;
  categoryname: string;
  displayorder: number;
}

// Simple cache to prevent multiple API calls
let allCodesCache: CodeCommon[] | null = null;
let inflightPromise: Promise<CodeCommon[]> | null = null;

const useCodeCommon = (categoryName: string, forceRefresh: boolean = false) => {
  const [options, setOptions] = useState<CodeCommon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCodeCommon = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let allCodes: CodeCommon[];
      
      // If forceRefresh is true, always fetch from API
      if (forceRefresh) {
        inflightPromise = (async () => {
          const response = await apiService.getAllCodeCommon();
          const raw = (response as any)?.data || response || [];
          return raw as CodeCommon[];
        })();
        allCodes = await inflightPromise;
        allCodesCache = allCodes;
        inflightPromise = null;
      } else if (allCodesCache) {
        allCodes = allCodesCache;
      } else if (inflightPromise) {
        // If already fetching, wait for that promise
        allCodes = await inflightPromise;
      } else {
        // First time fetching
        inflightPromise = (async () => {
          const response = await apiService.getAllCodeCommon();
          const raw = (response as any)?.data || response || [];
          return raw as CodeCommon[];
        })();
        allCodes = await inflightPromise;
        allCodesCache = allCodes;
        inflightPromise = null;
      }
      
      // Filter by categoryname
      const filteredCodes = allCodes.filter((code: any) => 
        code.categoryname === categoryName
      );
      
      // Sort by displayorder
      const sortedCodes = filteredCodes.sort((a: CodeCommon, b: CodeCommon) => 
        a.displayorder - b.displayorder
      );
      
      setOptions(sortedCodes);
    } catch (err: any) {
      console.error(`Error fetching ${categoryName} codes:`, err);
      setError(err.message || `Failed to fetch ${categoryName} options`);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [categoryName, forceRefresh]);

  useEffect(() => {
    fetchCodeCommon();
  }, [fetchCodeCommon]);

  return { options, loading, error, refetch: fetchCodeCommon };
};

export default useCodeCommon;
